import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import A from '@adsk/bim360-matrix-react-a';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

import './_Banner.scss';

import infoIcon from '@adsk/bim360-matrix-react-assets/info-circle-small.svg';
import closeIcon from '@adsk/bim360-matrix-react-assets/close-rounded-icon.svg';
import alloyClose from '@adsk/bim360-matrix-react-assets/alloy-close.svg';
import alloyAlertInformation from '@adsk/bim360-matrix-react-assets/alloy-alert-information.svg';
import alloyAlertSuccess from '@adsk/bim360-matrix-react-assets/alloy-alert-success.svg';
import alloyAlertWarning from '@adsk/bim360-matrix-react-assets/alloy-alert-warning.svg';
import alloyAlertError from '@adsk/bim360-matrix-react-assets/alloy-alert-error.svg';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

const BANNER_LEVELS = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success'
};

export const mapBimLevelToAlloyIcon = {
  [BANNER_LEVELS.INFO]: alloyAlertInformation,
  [BANNER_LEVELS.WARNING]: alloyAlertWarning,
  [BANNER_LEVELS.ERROR]: alloyAlertError,
  [BANNER_LEVELS.SUCCESS]: alloyAlertSuccess
};

class Banner extends PureComponent {
  getSvg = (level, theme) => {
    if (theme === Themes.ALLOY) {
      return mapBimLevelToAlloyIcon[level];
    }

    return infoIcon;
  };

  getClassname = (baseClass, theme) => {
    return cx(baseClass, `${baseClass}--${this.props.level}`, {
      [`${baseClass}--${theme}`]: theme
    });
  };

  renderClose = theme => {
    if (this.props.onClose) {
      if (theme === Themes.ALLOY) {
        return (
          <IconButton
            data-testid="Banner__close-button"
            className={this.getClassname('Banner__close')}
            onClick={this.props.onClose}
          >
            <SvgIcon>{alloyClose}</SvgIcon>
          </IconButton>
        );
      }

      return (
        <IconButton
          data-testid="Banner__close-button"
          className={this.getClassname('Banner__close')}
          onClick={this.props.onClose}
        >
          <SvgIcon>{closeIcon}</SvgIcon>
        </IconButton>
      );
    }

    return null;
  };

  renderActions = prefix => {
    if (this.props.actions) {
      const actions = Array.isArray(this.props.actions) ? this.props.actions : [this.props.actions];
      return (
        <div className="Banner__actions">
          {prefix}
          {actions.map(({ text, className, target, href, ...action }, index) => (
            /* eslint-disable no-script-url */
            <A
              key={`${text}--${index}`}
              className={cx(this.getClassname('Banner__action'), className)}
              target={target || '_blank'}
              href={href || 'javascript:void(0)'}
              {...action}
            >
              {text}
            </A>
            /* eslint-enable no-script-url */
          ))}
        </div>
      );
    }

    return null;
  };

  getContent(wordCount) {
    const trimmedContent = this.props.content.trim();
    const words = trimmedContent.split(/\b/);

    return this.props.actions
      ? {
        lastWords: words.slice(-2 * wordCount).join(''),
        withoutLastWords: words.slice(0, -2 * wordCount).join('')
      }
      : {
        lastWords: '',
        withoutLastWords: trimmedContent
      };
  }

  renderIcon = theme => {
    if (theme === Themes.ALLOY) {
      return (
        <div className="Banner__icon-holder">
          <SvgIcon width="24px" height="24px" className={this.getClassname('Banner__icon')}>
            {this.getSvg(this.props.level, theme)}
          </SvgIcon>
        </div>
      );
    }

    return (
      <SvgIcon className={this.getClassname('Banner__icon')}>
        {this.getSvg(this.props.level)}
      </SvgIcon>
    );
  };

  render() {
    const { lastWords, withoutLastWords } = this.getContent(1);

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div className={this.getClassname('Banner', theme)}>
            {this.renderIcon(theme)}
            <div className="Banner__content">
              <div className={this.getClassname('Banner__text-content', theme)}>
                {withoutLastWords}
                {this.renderActions(lastWords)}
              </div>
            </div>
            {this.renderClose(theme)}
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

Banner.Levels = BANNER_LEVELS;

Banner.defaultProps = {
  level: Banner.Levels.INFO
};

const actionShape = PropTypes.shape({
  className: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  target: PropTypes.string,
  text: PropTypes.node.isRequired
});

Banner.propTypes = {
  /**
   * The level of the banner, could be one of Banner.Levels.INFO, Banner.Levels.WARNING or Banner.Levels.ERROR
   */
  level: PropTypes.oneOf(objectValues(Banner.Levels)),
  /**
   * The main content to display for the banner.
   */
  content: PropTypes.node.isRequired,
  /**
   * Custom actions to show next to content.
   * Each action has a target property which defaults to '_blank'
   */
  actions: PropTypes.oneOfType([PropTypes.arrayOf(actionShape), actionShape]),
  /**
   * An action that's called when closing the banner, it also determines whetehr or not to show the close icon for the banner.
   */
  onClose: PropTypes.func
};
export default Banner;
